import React from 'react';
import PropTypes from 'prop-types';
import { Box } from 'rebass/styled-components';
import PolicyConditionProperty from './PolicyConditionProperty';
import PolicyConditionValue from './PolicyConditionValue';
import PolicyConditionType from './PolicyConditionType';
import PolicyActionType from './PolicyActionType';
import PolicyActionValue from './PolicyActionValue';
import PolicyActionFallbackSkill from './PolicyActionFallbackSkill';
import EditorDelete from '../Editor/EditorDelete';

function PolicyRow(props) {
  const {
    isEditing,
    rowIndex,
    temporaryPolicy,
    sectionType,
    rowItem,
    customAttributes,
    skills,
    agents,
    intents,
    channels,
    validateObject,
    isLast,
  } = props;

  const hasMultipleRows = temporaryPolicy[sectionType].length > 1;

  const deleteItem = ((index) => {
    const newObject = { ...temporaryPolicy };
    newObject[sectionType] = temporaryPolicy[sectionType].filter((item, i) => i !== index);
    validateObject(newObject);
  });

  const showFallback = (rowItem.type === 'TRANSFER_TO_AGENT')
    && (
      isEditing
      || (typeof rowItem.payload.skillId === 'string' && rowItem.payload.skillId !== '-1')
    );

  return (
    <>
      <Box
        alignItems="center"
        data-testid={`editor-row-${sectionType}`}
        px={2}
        pt={2}
        pb={isLast || showFallback ? 2 : 0}
        sx={{
          display: 'grid',
          gridGap: 2,
          gridTemplateColumns: sectionType === 'conditions' ? `${isEditing ? '1fr' : 'auto'} auto 2fr` : '1fr 2fr',
        }}
      >
        {(sectionType === 'conditions') && (
          <>
            <PolicyConditionProperty
              rowItem={rowItem}
              rowIndex={rowIndex}
              isEditing={isEditing}
              temporaryPolicy={temporaryPolicy}
              customAttributes={customAttributes}
              validateObject={validateObject}
            />
            <PolicyConditionType
              rowItem={rowItem}
              rowIndex={rowIndex}
              isEditing={isEditing}
              validateObject={validateObject}
              temporaryPolicy={temporaryPolicy}
              agents={agents}
            />
            <Box
              sx={{
                display: 'grid',
                gridGap: 2,
                gridTemplateColumns: isEditing && hasMultipleRows ? '1fr 2fr auto' : '1fr 2fr',
                alignItems: 'center',
              }}
            >
              <PolicyConditionValue
                rowItem={rowItem}
                rowIndex={rowIndex}
                isEditing={isEditing}
                temporaryPolicy={temporaryPolicy}
                skills={skills}
                customAttributes={customAttributes}
                agents={agents}
                intents={intents}
                channels={channels}
                validateObject={validateObject}
              />
              {isEditing && hasMultipleRows && (
                <EditorDelete rowIndex={rowIndex} deleteItem={deleteItem} icon="close" />
              )}
            </Box>
          </>
        )}
        {(sectionType === 'actions') && (
          <>
            <PolicyActionType
              rowItem={rowItem}
              rowIndex={rowIndex}
              isEditing={isEditing}
              validateObject={validateObject}
              temporaryPolicy={temporaryPolicy}
              customAttributes={customAttributes}
            />
            <Box
              sx={{
                display: 'grid',
                gridGap: 2,
                gridTemplateColumns: `1fr${isEditing ? ' 2fr' : ''} ${hasMultipleRows ? ' auto' : ''}`,
                alignItems: 'center',
              }}
              data-testid="policy-action-value-wrapper"
            >
              <PolicyActionValue
                rowItem={rowItem}
                rowIndex={rowIndex}
                isEditing={isEditing}
                temporaryPolicy={temporaryPolicy}
                skills={skills}
                customAttributes={customAttributes}
                agents={agents}
                validateObject={validateObject}
              />
              {isEditing && hasMultipleRows && (
                <EditorDelete rowIndex={rowIndex} deleteItem={deleteItem} icon="close" />
              )}
            </Box>
          </>
        )}
      </Box>
      {showFallback && (
        <PolicyActionFallbackSkill
          rowItem={rowItem}
          rowIndex={rowIndex}
          isEditing={isEditing}
          temporaryPolicy={temporaryPolicy}
          skills={skills}
          customAttributes={customAttributes}
          validateObject={validateObject}
          isLast={isLast}
        />
      )}
    </>
  );
}

PolicyRow.defaultProps = {
  isEditing: false,
};

PolicyRow.propTypes = {
  isLast: PropTypes.bool.isRequired,
  customAttributes: PropTypes.array.isRequired,
  skills: PropTypes.array.isRequired,
  agents: PropTypes.array.isRequired,
  intents: PropTypes.array.isRequired,
  channels: PropTypes.array.isRequired,
  rowItem: PropTypes.object.isRequired,
  isEditing: PropTypes.bool,
  rowIndex: PropTypes.number.isRequired,
  temporaryPolicy: PropTypes.object.isRequired,
  sectionType: PropTypes.string.isRequired,
  validateObject: PropTypes.func.isRequired,
};

export default PolicyRow;
